import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { Pencil, Plus } from 'lucide-react';
import LeiderschapLayout from '@/components/LeiderschapLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { leiderschapApi } from '../api';
import SectionHeader from '../components/SectionHeader';
import ConfidentialBadge from '../components/ConfidentialBadge';
import { DECISION_STATUSES, findClassName, findLabel } from '../labels';
import type { Confidentiality, Decision, DecisionInput, DecisionStatus } from '../types';

interface FormState {
  title: string;
  description: string;
  category: string;
  status: DecisionStatus;
  effective_date: string;
  confidentiality: Confidentiality;
}

const EMPTY_FORM: FormState = {
  title: '',
  description: '',
  category: '',
  status: 'open',
  effective_date: '',
  confidentiality: 'normaal',
};

export default function DecisionsPage() {
  const { hasCapability } = useAuth();
  const canManage = hasCapability('leiderschap_manage');

  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<DecisionStatus | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDecision, setEditingDecision] = useState<Decision | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const fetchDecisions = async () => {
    try {
      const data = await leiderschapApi.listDecisions();
      setDecisions(data);
    } catch (error) {
      toast.error('Kon besluiten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDecisions();
  }, []);

  const filteredDecisions = useMemo(
    () =>
      decisions
        .filter((d) => statusFilter === 'all' || d.status === statusFilter)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [decisions, statusFilter],
  );

  const openCreateDialog = () => {
    setEditingDecision(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (decision: Decision) => {
    setEditingDecision(decision);
    setForm({
      title: decision.title,
      description: decision.description || '',
      category: decision.category || '',
      status: decision.status,
      effective_date: decision.effective_date || '',
      confidentiality: decision.confidentiality,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title) return;
    const payload: DecisionInput = {
      title: form.title,
      description: form.description || null,
      category: form.category || null,
      status: form.status,
      effective_date: form.effective_date || null,
      confidentiality: form.confidentiality,
    };
    try {
      if (editingDecision) {
        await leiderschapApi.updateDecision(editingDecision.id, payload);
        toast.success('Besluit bijgewerkt');
      } else {
        await leiderschapApi.createDecision(payload);
        toast.success('Besluit geregistreerd');
      }
      setDialogOpen(false);
      fetchDecisions();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  return (
    <LeiderschapLayout>
      <SectionHeader
        title="Besluiten"
        description="Vastgelegde besluiten, los van of gekoppeld aan een vergadering."
        action={canManage && (
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuw besluit
          </Button>
        )}
      />

      <div className="mb-4">
        <Select value={statusFilter} onValueChange={(value: DecisionStatus | 'all') => setStatusFilter(value)}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {DECISION_STATUSES.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {!loading && filteredDecisions.length === 0 && (
            <p className="p-6 text-sm text-slate-500">Geen besluiten gevonden.</p>
          )}
          <div className="divide-y divide-slate-100">
            {filteredDecisions.map((decision) => (
              <div key={decision.id} className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900">{decision.title}</p>
                    <Badge className={findClassName(DECISION_STATUSES, decision.status)}>
                      {findLabel(DECISION_STATUSES, decision.status)}
                    </Badge>
                    <ConfidentialBadge confidentiality={decision.confidentiality} />
                  </div>
                  {decision.description && <p className="mt-1 text-sm text-slate-500">{decision.description}</p>}
                  <p className="mt-1 text-xs text-slate-400">
                    {decision.category && `${decision.category} · `}
                    {format(parseISO(decision.created_at), 'd MMM yyyy', { locale: nl })}
                  </p>
                </div>
                {canManage && (
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(decision)} aria-label="Bewerken">
                    <Pencil className="h-4 w-4 text-slate-400" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDecision ? 'Besluit bewerken' : 'Nieuw besluit'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="decision-title">Titel</Label>
              <Input
                id="decision-title"
                value={form.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="decision-description">Toelichting</Label>
              <Textarea
                id="decision-description"
                value={form.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, description: e.target.value })}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="decision-category">Categorie</Label>
                <Input
                  id="decision-category"
                  value={form.category}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, category: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="decision-status">Status</Label>
                <Select value={form.status} onValueChange={(value: DecisionStatus) => setForm({ ...form, status: value })}>
                  <SelectTrigger id="decision-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DECISION_STATUSES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="decision-effective-date">Ingangsdatum</Label>
              <Input
                id="decision-effective-date"
                type="date"
                value={form.effective_date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, effective_date: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="decision-confidential"
                checked={form.confidentiality === 'vertrouwelijk'}
                onCheckedChange={(checked: boolean) =>
                  setForm({ ...form, confidentiality: checked ? 'vertrouwelijk' : 'normaal' })
                }
              />
              <Label htmlFor="decision-confidential" className="font-normal">
                Vertrouwelijk -- alleen zichtbaar voor de aanmaker en admin
              </Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
              <Button type="submit">{editingDecision ? 'Opslaan' : 'Registreren'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </LeiderschapLayout>
  );
}
